import { Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { TranslateBlockDirective } from '@ngx-translate/core';
import { DeviceProfileService } from '../../../../../../../services/device-profile.service';
import { DeviceService } from '../../../../../../../services/device.service';
import { InputItemSelection } from '../../../../../../../shared/components/input-item-selection/input-item-selection';
import { InputSelectComponent } from '../../../../../../../shared/components/input-select/input-select';
import { InputTextComponent } from '../../../../../../../shared/components/input-text/input-text.component';
import { TableComponent } from '../../../../../../../shared/components/table/table';
import { formatTimeAgo } from '../../../../../../../shared/utils/date.util';
import { DeviceDto, DeviceRow } from '../../../../../../../types/dtos/device.dto';
import { DeviceProfileDto } from '../../../../../../../types/dtos/deviceProfile.dto';
import { DeviceStatus } from '../../../../../../../types/enums/device.enum';
import {
  InputItemSelectInterface,
  InputSelectInterface,
  InputTextInterface,
} from '../../../../../../../types/interfaces/input.interface';
import { TableConfig, TableRowDto, TableTagSeverity } from '../../../../../../../types/interfaces/table.interface';
import { DashboardBoxComponent } from '../../../../../../dashboard/dashboard-box/dashboard-box.component';

@Component({
  selector: 'app-report-select-device',
  imports: [
    TableComponent,
    DashboardBoxComponent,
    InputSelectComponent,
    InputTextComponent,
    InputItemSelection,
    TranslateBlockDirective,
  ],
  templateUrl: './select-device.html',
  styleUrl: './select-device.css',
})
export class ReportSelectDevice {
  @Input({required: true}) formGroup!: FormGroup;

  public loading: boolean = true;
  public allDevices: DeviceDto[] = [];
  public deviceList: DeviceDto[] = [];
  public tableRows: TableRowDto<DeviceRow>[] = [];
  public tableConfigFilter: TableConfig<DeviceRow> = {
    columns: [
      {
        key: 'status',
        title: 'common.fields.status',
        isTranslated: true,
        isTag: true,
        getTagColor: (row: DeviceRow) => row.status === 'online'
          ? TableTagSeverity.SUCCESS
          : TableTagSeverity.DANGER,
        overrideValue: (row: DeviceRow) => row.status === 'online' ? 'Online' : 'Offline',
      },
      {
        key: 'serial_number',
        title: 'common.table.serielNumber.label',
        isTranslated: true,
      },
      {
        key: 'device_eui',
        title: 'common.fields.device-eui',
        isTranslated: true,
        visible: false,
      },
      {
        key: 'model',
        title: 'common.fields.model',
        isTranslated: true,
      },
      {
        key: 'address',
        title: 'common.fields.address',
        isTranslated: true,
      },
      {
        key: 'last_measurement',
        title: 'common.fields.last-measurement',
        isTranslated: true,
        overrideValue: (row: DeviceRow) => {
          if (!row.last_measurement) {
            return '-';
          }

          let lastMeasurementTime = formatTimeAgo(row.last_measurement_received_time);

          if (!lastMeasurementTime || lastMeasurementTime === '-') {
            return `${ (row.last_measurement as number) / 1000 } m³`;
          }

          lastMeasurementTime = lastMeasurementTime.charAt(0)
            .toLowerCase() + lastMeasurementTime.slice(1);

          return `${ (row.last_measurement as number) / 1000 } m³ ${ lastMeasurementTime }`;
        },
      },
    ],
    mobile: {
      title: {
        key: 'serial_number',
        tag: 'common.fields.status',
        getTagColor: (row: DeviceRow) => row.status === 'online'
          ? TableTagSeverity.SUCCESS
          : TableTagSeverity.DANGER,
        overrideTag: (row: DeviceRow) => row.status === 'online' ? 'Online' : 'Offline',
      },
      description: {
        key: 'model',
      },
      sections: [
        {
          key: 'address',
          isTranslated: true,
          title: 'common.fields.address',
        },
        {
          key: 'last_measurement',
          isTranslated: true,
          title: 'common.fields.last-measurement',
          overrideValue: (row: DeviceRow) => {
            if (!row.last_measurement) {
              return '-';
            }

            let lastMeasurementTime = formatTimeAgo(row.last_measurement_received_time);

            if (!lastMeasurementTime || lastMeasurementTime === '-') {
              return `${ (row.last_measurement as number) / 1000 } m³`;
            }

            lastMeasurementTime = lastMeasurementTime.charAt(0)
              .toLowerCase() + lastMeasurementTime.slice(1);

            return `${ (row.last_measurement as number) / 1000 } m³ ${ lastMeasurementTime }`;
          },
        },
      ],
    },
    search: {
      label: 'Suche'
    },
    pagination: true,
    showRows: 25,
    rowsPerPageOptions: [
      25,
      50,
      100,
    ],
    scrollHeight: '55vh',
    emptyMessage: 'common.messages.empty.data',
    lazy: false,
    sortableColumns: true,
  };


  public tableConfigSelect: TableConfig<DeviceRow> = {
    ...this.tableConfigFilter,
    selectable: {
      onUpdate: (rows: DeviceRow[]) => {
        let ids: number[] = rows.map((row: DeviceRow) => row.id);
        this.formGroup.get('ids')?.setValue(ids.length ? ids : null);
      }
    }
  }

  private modelOptions: InputSelectInterface['options'] = [];
  private _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private _deviceService: DeviceService,
    private _deviceProfileService: DeviceProfileService,
  ) {
  }

  public get typeConfig(): InputItemSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.select-device-filter-type.label',
      id: 'typeSelect',
      iconLeft: 'account_circle',
      options: [
        {
          isTranslated: true,
          label: 'common.input.select-device-filter-type.values.select-device',
          value: 'select',
          icon: 'arrow_selector_tool'
        },
        {
          isTranslated: true,
          label: 'common.input.select-device-filter-type.values.filter-device',
          value: 'filter',
          icon: 'filter_arrow_right'
        }
      ],
      required: false,
      onInput: () => this._filterData(),
    };
  }

  public get statusSelect(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      onInput: () => this._filterData(),
      isTranslated: true,
      label: 'common.input.status-select.label',
      id: 'statusSelect',
      iconLeft: 'account_circle',
      showClear: true,
      options: Object.values(DeviceStatus)
        .sort()
        .map((action) => ({
          isTranslated: true,
          label: 'common.enum.status.' + action,
          value: action.toLowerCase(),
        })),
      required: false,
    };
  };

  public get modelSelect(): InputSelectInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.model.label',
      id: 'modelSelect',
      iconLeft: 'account_circle',
      showClear: true,
      options: this.modelOptions,
      required: false,
      onInput: () => this._filterData(),
    };
  };

  public get searchConfig(): InputTextInterface {
    return {
      formGroup: this.formGroup,
      isTranslated: true,
      label: 'common.input.search-device.label',
      id: 'search',
      iconLeft: 'search',
      showClear: true,
      type: 'text',
      onInput: () => this._filterData(),
    };
  };

  public get selectSelectType(): string {
    return this.formGroup.get('selectType')?.value ?? '';
  }

  public onTypeSelect(event: string): void {
    this.formGroup.get('selectType')?.setValue(event);
  }

  public get deviceCountLabel(): string {
    return `${ this.deviceList.length } von ${ this.allDevices.length } Geräte`;
  }

  public resetFilter(): void {
    this.formGroup.reset();
    this._filterData();
  }

  public ngAfterViewInit(): void {
    if (!this.formGroup.get('selectType')?.value) {
      this.formGroup.get('selectType')?.setValue(null);
    }

    this._deviceService.devices$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (data: DeviceDto[]) => {
          this.allDevices = data;
          this._filterData();
          this.loading = false;
        },
        error: (error: unknown) => {
          console.error('Error fetching devices:', error);
        },
      });

    this._deviceProfileService.devicesProfile$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((devicesProfile: DeviceProfileDto[]) => {
        this.modelOptions = [
          {
            label: 'Ohne Model',
            value: '-1',
          },
          ...devicesProfile
            .filter((profile: DeviceProfileDto) => profile.model)
            .map((profile: DeviceProfileDto) => ({
              label: profile.model ?? '',
              value: profile.id.toString(),
            })),
        ];
      });
  }


  public getDeviceModelName(deviceProfileId: number): string {
    const deviceProfile: DeviceProfileDto | null =
      this._deviceProfileService.getDeviceProfileById(deviceProfileId);

    return deviceProfile?.model ?? '-';
  }

  private _setTableRows(): void {
    this.tableConfigFilter.totalRecords = this.deviceList.length;
    this.tableConfigSelect.totalRecords = this.deviceList.length;
    this.tableRows = this.deviceList.map((device: DeviceDto) => {
      return {
        id: device.id,
        status: device.status,
        device_eui: device.device_eui,
        serial_number: device.serial_number ?? '-',
        model: device.device_profile_id ? this.getDeviceModelName(device.device_profile_id) : '-',
        address: device.position?.address ?? '-',
        alarms: 0,
        last_measurement: device.last_measurement?.['METER_VALUE_1']
          ? Number(device.last_measurement['METER_VALUE_1'])
          : null,
        last_measurement_received_time: device.last_measurement_received_time ?? undefined,
        last_sigi_send_time: device.last_sigi_send_time ?? undefined,
      };
    });
  }

  private _filterData(): void {
    let {
      statusSelect,
      modelSelect,
      search,
    } = this.formGroup.value;

    modelSelect = Number(modelSelect);

    const normalizedSearch: string = String(search ?? '')
      .trim()
      .toLowerCase();

    this.deviceList = this.allDevices.filter((device: DeviceDto) => {
      const matchesStatus: boolean = !statusSelect || device.status === statusSelect;
      const matchesModel: boolean = !modelSelect || (device.device_profile_id ?? -1) === modelSelect;

      const deviceEui: string = device.device_eui ?? '';
      const serialNumber: string = device.serial_number ?? '';
      const model: string = device.device_profile_id
        ? this.getDeviceModelName(device.device_profile_id)
        : '-';
      const address: string = device.position?.address ?? '-';
      const status: string = device.status ?? '';

      const matchesSearch: boolean = !normalizedSearch || [
        deviceEui,
        serialNumber,
        model,
        address,
        status,
      ].some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(normalizedSearch),
      );

      return matchesStatus && matchesModel && matchesSearch;
    });

    this._setTableRows();
  }
}
